import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, LogOut, Package, RefreshCw, AlertTriangle, ShoppingBag, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ProductEditDrawer from '../components/admin/ProductEditDrawer';
import OrdersPanel from '../components/admin/OrdersPanel';
import BlogPanel from '../components/admin/BlogPanel';
import { invalidateProductsCache } from '../services/products';

const PRODUCT_CLASSES = [
  { id: 'complete-scooters', name: 'Tõuksid' },
  { id: 'decks', name: 'Tallad' },
  { id: 'bars', name: 'Lenksud' },
  { id: 'wheels', name: 'Rattad' },
  { id: 'forks', name: 'Kahvlid' },
  { id: 'clamps', name: 'Klambrid' },
  { id: 'bar-ends', name: 'Lenksuotsad' },
  { id: 'pegs', name: 'Peegid' },
  { id: 'grips', name: 'Käepidemed' },
  { id: 'griptape', name: 'Gripteibid' },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [tab, setTab] = useState<'products' | 'orders' | 'blog'>('products');

  // Auth guard
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/admin/login', { replace: true });
      } else {
        loadProducts();
      }
    });
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: dbError } = await supabase
        .from('products')
        .select('*, product_variants(*)')
        .order('brand');

      if (dbError) throw dbError;

      const processed = (data || []).map((p: any) => ({
        ...p,
        variants: (p.product_variants || []).sort((a: any, b: any) => a.price - b.price),
      }));

      setProducts(processed);
    } catch (err: any) {
      setError(err.message || 'Toodete laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  const brands = useMemo(() => {
    const set = new Set(products.map((p: any) => p.brand).filter(Boolean));
    return Array.from(set).sort() as string[];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p: any) => {
      if (filterClass && p.class !== filterClass) return false;
      if (filterBrand && p.brand !== filterBrand) return false;
      if (search) {
        const q = search.toLowerCase();
        const matchBrand = p.brand?.toLowerCase().includes(q);
        const matchVariant = p.variants?.some((v: any) => v.name?.toLowerCase().includes(q));
        if (!matchBrand && !matchVariant) return false;
      }
      return true;
    });
  }, [products, filterClass, filterBrand, search]);

  const getDisplayPrice = (product: any) => {
    const prices = (product.variants || []).map((v: any) => v.discounted_price || v.price).filter(Boolean);
    if (!prices.length) return '—';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? `${min.toFixed(2)}€` : `${min.toFixed(2)}–${max.toFixed(2)}€`;
  };

  const getTotalStock = (product: any) =>
    (product.variants || []).reduce((sum: number, v: any) => sum + (v.stock || 0), 0);

  const className = (cls: string) =>
    PRODUCT_CLASSES.find(c => c.id === cls)?.name || cls;

  return (
    <>
      <Helmet>
        <title>Admin — Tõuks</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen text-white flex flex-col relative">
        {/* Shop animated background */}
        <div className="geometric-bg" />
        <div className="scooter-pattern" />
        <div className="dynamic-lines" />
        <div className="scooter-tracks" />

        {/* ── Top bar ── */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 px-6 py-3.5 flex items-center gap-4 sticky top-0 z-40 flex-shrink-0">
          <img src="https://kvantis.ee/Touks.png" alt="Tõuks" className="h-9" />
          <span className="text-gray-500 text-xs">Admin</span>

          {/* Tab switcher */}
          <div className="flex items-center gap-1 ml-4 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setTab('products')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                tab === 'products' ? 'bg-white text-[#4FC1E9] shadow-sm' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Tooted</span>
            </button>
            <button
              onClick={() => setTab('orders')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                tab === 'orders' ? 'bg-white text-[#4FC1E9] shadow-sm' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Tellimused</span>
            </button>
            <button
              onClick={() => setTab('blog')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                tab === 'blog' ? 'bg-white text-[#4FC1E9] shadow-sm' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Blogi</span>
            </button>
          </div>

          {/* Product search (products tab only) */}
          {tab === 'products' && (
            <div className="flex-1 max-w-sm relative ml-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Otsi brändi või toodet..."
                className="w-full bg-white border border-gray-300 rounded-xl pl-9 pr-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition"
              />
            </div>
          )}

          <div className="flex items-center gap-4 ml-auto">
            {tab === 'products' && (
              <span className="text-gray-400 text-sm hidden sm:block">
                {filtered.length} / {products.length} toodet
              </span>
            )}
            {tab === 'products' && (
              <button
                onClick={loadProducts}
                className="p-2 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition"
                title="Uuenda"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm transition"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logi välja</span>
            </button>
          </div>
        </header>

        <div className="flex flex-1">

          {/* ── Sidebar filters (products only) ── */}
          {tab === 'products' && (
          <aside className="w-52 bg-white/70 backdrop-blur-sm border-r border-gray-200 flex-shrink-0">
            <div className="p-4 space-y-6">

              {/* Product class filter */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Toote tüüp</h3>
                <div className="space-y-0.5">
                  <FilterBtn active={!filterClass} onClick={() => setFilterClass('')}>Kõik</FilterBtn>
                  {PRODUCT_CLASSES.map(cls => (
                    <FilterBtn
                      key={cls.id}
                      active={filterClass === cls.id}
                      onClick={() => setFilterClass(filterClass === cls.id ? '' : cls.id)}
                    >
                      {cls.name}
                    </FilterBtn>
                  ))}
                </div>
              </div>

              {/* Brand filter */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Bränd</h3>
                <div className="space-y-0.5">
                  <FilterBtn active={!filterBrand} onClick={() => setFilterBrand('')}>Kõik</FilterBtn>
                  {brands.map(brand => (
                    <FilterBtn
                      key={brand}
                      active={filterBrand === brand}
                      onClick={() => setFilterBrand(filterBrand === brand ? '' : brand)}
                    >
                      {brand}
                    </FilterBtn>
                  ))}
                </div>
              </div>
            </div>
          </aside>
          )}

          {/* ── Main content ── */}
          <main className="flex-1 p-6">

            {tab === 'orders' && <OrdersPanel />}

            {tab === 'blog' && <BlogPanel />}

            {tab === 'products' && loading && (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                Laen tooteid...
              </div>
            )}

            {tab === 'products' && error && !loading && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {tab === 'products' && !loading && !error && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                {filtered.map((product: any) => {
                  const firstImg = product.variants?.[0]?.images?.[0];
                  const stock = getTotalStock(product);
                  const lowStock = product.variants?.some((v: any) => v.stock > 0 && v.stock < 5);
                  const outOfStock = stock === 0;

                  return (
                    <div
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:border-[#4FC1E9] hover:shadow-lg hover:shadow-[#4FC1E9]/10 transition-all duration-200 group"
                    >
                      {/* Image */}
                      <div className="relative aspect-square bg-white">
                        {firstImg ? (
                          <img
                            src={firstImg}
                            alt={product.brand}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-10 h-10 text-gray-700" />
                          </div>
                        )}

                        {/* Stock badge */}
                        {outOfStock && (
                          <span className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            0 tk
                          </span>
                        )}
                        {!outOfStock && lowStock && (
                          <span className="absolute top-2 right-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            Vähe
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-400 truncate">{className(product.class)}</span>
                          <div className="flex gap-0.5 flex-shrink-0 ml-1">
                            {(product.variants || []).slice(0, 5).map((v: any) => (
                              <div
                                key={v.id}
                                className="w-3 h-3 rounded-full border border-gray-600"
                                style={{ backgroundColor: v.color_code || '#666' }}
                                title={v.color}
                              />
                            ))}
                            {(product.variants || []).length > 5 && (
                              <span className="text-xs text-gray-600 ml-0.5">+{product.variants.length - 5}</span>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-900 font-semibold text-sm truncate">{product.brand}</p>
                        <p className="text-[#4FC1E9] text-sm font-bold mt-0.5">{getDisplayPrice(product)}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {stock} tk · {(product.variants || []).length} varianti
                        </p>
                      </div>
                    </div>
                  );
                })}

                {filtered.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center h-48 text-gray-600">
                    <Package className="w-12 h-12 mb-3 opacity-30" />
                    <p>Tooteid ei leitud</p>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Product edit drawer */}
      {selectedProduct && (
        <ProductEditDrawer
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onSaved={() => {
            setSelectedProduct(null);
            invalidateProductsCache();
            loadProducts();
          }}
        />
      )}
    </>
  );
}

function FilterBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
        active
          ? 'bg-[#4FC1E9]/15 text-[#4FC1E9] font-medium'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}
